/**
 * QuestionBuilder — edit the attestation's question set like a document,
 * not like a form-builder science project.
 *
 * The nine CMS elements are the spine; the org appends its own questions or
 * edits any row in place. Response types are deliberately few: Yes/No, short
 * answer, number, date, pick one, pick many. One level of logic only: "when
 * the answer is X, require a written explanation." Changes apply to
 * attestations sent AFTER saving — anything already sent keeps the exact
 * question set it went out with (that snapshot is what gets signed).
 */
import React, { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

const TYPE_META = {
  yes_no:       { label: 'Confirm / Cannot confirm', hint: 'The classic attestation element' },
  text:         { label: 'Short answer',             hint: 'A written response' },
  number:       { label: 'Number',                   hint: 'e.g. headcount, days, percent' },
  date:         { label: 'Date',                     hint: 'e.g. last training date' },
  select:       { label: 'Pick one',                 hint: 'One choice from your list' },
  multi_select: { label: 'Pick many',                hint: 'Any of your listed choices' },
};

function emptyDraft() {
  return { label: '', help_text: '', response_type: 'yes_no', optionsText: '', required: true, requires_explanation_on: 'no' };
}

function QuestionForm({ initial, onSave, onCancel, saving }) {
  const [d, setD] = useState(initial);
  const isChoice = d.response_type === 'select' || d.response_type === 'multi_select';
  const options = d.optionsText.split('\n').map(s => s.trim()).filter(Boolean);
  const explainChoices = d.response_type === 'yes_no' ? ['no'] : isChoice ? options : [];

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">The question</label>
        <textarea value={d.label} onChange={e => setD({ ...d, label: e.target.value })} rows={2} autoFocus
          placeholder="e.g. Subcontractor monitoring reports are submitted to the sponsor quarterly."
          className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Answer type</label>
          <select value={d.response_type}
            onChange={e => {
              const t = e.target.value;
              setD({ ...d, response_type: t, requires_explanation_on: t === 'yes_no' ? 'no' : '' });
            }}
            className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2">
            {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="text-[11px] text-slate-400 mt-1">{TYPE_META[d.response_type].hint}</div>
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Help text (optional)</label>
          <input value={d.help_text} onChange={e => setD({ ...d, help_text: e.target.value })}
            placeholder="Shown under the question in the portal"
            className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
        </div>
      </div>
      {isChoice && (
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Choices — one per line</label>
          <textarea value={d.optionsText} onChange={e => setD({ ...d, optionsText: e.target.value })} rows={3}
            placeholder={'Quarterly\nSemi-annually\nAnnually'}
            className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 font-mono" />
        </div>
      )}
      <div className="flex items-center gap-5 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={d.required} onChange={e => setD({ ...d, required: e.target.checked })}
            className="rounded border-slate-300 text-orange-600" />
          Required
        </label>
        {explainChoices.length > 0 && (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Require a written explanation when the answer is
            <select value={d.requires_explanation_on} onChange={e => setD({ ...d, requires_explanation_on: e.target.value })}
              className="text-sm border border-slate-300 rounded-lg px-2 py-1">
              <option value="">never</option>
              {explainChoices.map(c => <option key={c} value={c}>{d.response_type === 'yes_no' ? '"Cannot confirm"' : `"${c}"`}</option>)}
            </select>
          </label>
        )}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button disabled={saving || d.label.trim().length < 5 || (isChoice && options.length < 2)}
          onClick={() => onSave({
            label: d.label.trim(), help_text: d.help_text.trim() || null, response_type: d.response_type,
            options: isChoice ? options : null, required: d.required,
            requires_explanation_on: d.requires_explanation_on || null,
          })}
          className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
          {saving ? 'Saving…' : 'Save question'}
        </button>
      </div>
    </div>
  );
}

export default function QuestionBuilder({ onClose }) {
  const [questions, setQuestions] = useState([]);
  const [custom, setCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/fdr/attestation-questions`, { credentials: 'include' }).then(r => r.json());
      if (r.success) { setQuestions(r.questions || []); setCustom(!!r.custom); }
    } catch { /* keep prior */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const call = async (path, method, body) => {
    setSaving(true); setError('');
    const r = await fetch(`${API}/api/fdr/attestation-questions${path}`, {
      method, credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then(r => r.json()).catch(() => ({ success: false, message: 'Network error' }));
    setSaving(false);
    if (!r.success) { setError(r.message || 'Something went wrong'); return null; }
    await load();
    return r;
  };

  const move = async (idx, dir) => {
    const next = [...questions];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setQuestions(next);
    await call('/reorder', 'POST', { ids: next.map(q => q.id) });
  };

  const toDraft = (q) => ({
    label: q.label, help_text: q.help_text || '', response_type: q.response_type,
    optionsText: Array.isArray(q.options) ? q.options.join('\n') : '',
    required: q.required !== false, requires_explanation_on: q.requires_explanation_on || '',
  });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-bold text-slate-800">Attestation questions</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {custom
                ? 'Your organization\'s question set. '
                : 'The standard CMS Chapter 21 set — your first edit makes your organization\'s own copy. '}
              Changes apply to attestations sent from now on; anything already sent keeps the exact questions it went out with.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-lg leading-none">✕</button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-2">
          {error && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
          {loading ? <div className="text-sm text-slate-400 py-6 text-center">Loading…</div> : questions.map((q, i) => (
            editingId === q.id ? (
              <QuestionForm key={q.id} initial={toDraft(q)} saving={saving}
                onCancel={() => setEditingId(null)}
                onSave={async (body) => { const r = await call(`/${q.id}`, 'PATCH', body); if (r) setEditingId(null); }} />
            ) : (
              <div key={q.id} className="group flex items-start gap-3 rounded-xl border border-slate-100 hover:border-slate-200 px-3 py-2.5">
                <div className="flex flex-col gap-0.5 pt-0.5">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-300 hover:text-slate-500 disabled:opacity-30 leading-none">▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === questions.length - 1} className="text-slate-300 hover:text-slate-500 disabled:opacity-30 leading-none">▼</button>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-700">{i + 1}. {q.label}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-slate-50 text-slate-500 border-slate-200">{TYPE_META[q.response_type]?.label || q.response_type}</span>
                    {q.required !== false && <span className="text-[10px] text-slate-400">required</span>}
                    {q.requires_explanation_on && <span className="text-[10px] text-amber-600">explains on "{q.requires_explanation_on === 'no' ? 'Cannot confirm' : q.requires_explanation_on}"</span>}
                    {q.is_system && <span className="text-[10px] text-slate-400">CMS standard</span>}
                    {Array.isArray(q.options) && q.options.length > 0 && <span className="text-[10px] text-slate-400">{q.options.length} choices</span>}
                  </div>
                  {q.help_text && <div className="text-[11px] text-slate-400 mt-0.5">{q.help_text}</div>}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button onClick={() => { setEditingId(q.id); setAdding(false); }}
                    className="px-2 py-1 text-[11px] font-semibold rounded-md text-slate-500 hover:bg-slate-100">Edit</button>
                  <button onClick={async () => {
                      if (!window.confirm(`Remove "${q.label.slice(0, 60)}…"? Attestations already sent are unaffected.`)) return;
                      await call(`/${q.id}`, 'DELETE');
                    }}
                    className="px-2 py-1 text-[11px] font-semibold rounded-md text-red-400 hover:bg-red-50 hover:text-red-600">Remove</button>
                </div>
              </div>
            )
          ))}

          {notice && <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{notice}</div>}
          {adding
            ? <QuestionForm initial={emptyDraft()} saving={saving}
                onCancel={() => setAdding(false)}
                onSave={async (body) => { const r = await call('', 'POST', body); if (r) setAdding(false); }} />
            : (
              <div className="space-y-2">
                <button onClick={() => { setAdding(true); setEditingId(null); }}
                  className="w-full text-center border border-dashed border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-400 hover:border-slate-300 hover:text-slate-600">
                  + Add a question
                </button>
                <button disabled={saving}
                  onClick={async () => {
                    setNotice('');
                    const r = await call('/insert-ai-set', 'POST');
                    if (r) setNotice(r.added > 0
                      ? `Added ${r.added} Responsible AI question${r.added === 1 ? '' : 's'}. Reorder or edit them like any other question.`
                      : 'The Responsible AI set is already in your questions.');
                  }}
                  className="w-full text-center border border-dashed border-orange-200 rounded-xl px-4 py-3 text-sm text-orange-500 hover:border-orange-300 hover:text-orange-600 disabled:opacity-50">
                  + Insert the Responsible AI question set
                  <span className="block text-[11px] text-slate-400 mt-0.5 font-normal">
                    Twelve authored questions — AI disclosure, human review of denials, non-discrimination, PHI and training data, change notice, incident contact. Applies to attestations sent from now on.
                  </span>
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
